import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchTree } from "../../api/treeClient";
import type { EmbedTreeResponse, EmbedProductDto } from "../../types/tree";
import {
  ConfiguratorViewer,
  type ConfiguratorViewerHandle,
} from "../../components/ConfiguratorViewer";
import { autoResolve, buildPreferenceMap, indexTree } from "../../tree/autoResolve";

export interface ProductTreePreviewToolProps {
  treeId: string;
  /** Same-origin if omitted. */
  baseUrl?: string;
  /** Optional Unity target — when set, the viewer pane loads. */
  target?: string;
  stage?: "Development" | "Staging" | "Production";
  /**
   * API base URL the Unity build uses for its own asset/material fetches.
   * Defaults to the page's origin. Override when the React app and the API are
   * on different origins (cross-origin embedding).
   */
  apiBaseUrl?: string;
  /**
   * Author-only flag for in-dashboard previews. When true the server returns the
   * unpruned tree (drafts + dead branches). Server only honors it for
   * dashboard-authenticated requests.
   */
  includeUnpublished?: boolean;
}

// PascalCase to match Unity's MaterialBinding fields (JsonUtility is
// case-sensitive). Same shape as the spawn-bundle's defaultBindings.
interface SurfaceBinding {
  Key: string;
  DmatId: string;
}

/**
 * Translates the user's picked material row id into the DMatAsset id Unity needs.
 * Unity's ProductLoader.FetchDMat hits /api/asset/material/{dmatId} which expects
 * the asset id (material.asset_id), not the material row id (material.id).
 */
function buildBindings(product: EmbedProductDto, materialPicks: Record<string, string>): SurfaceBinding[] {
  const out: SurfaceBinding[] = [];
  for (const s of product.surfaces) {
    const pickedMaterialId = materialPicks[s.surfaceKey] ?? s.defaultMaterialId;
    if (!pickedMaterialId) continue;
    const picked = s.materials.find(m => m.id === pickedMaterialId);
    if (!picked?.dmatAssetId) continue; // skip materials with no asset — Unity can't load them
    out.push({ Key: s.surfaceKey, DmatId: picked.dmatAssetId });
  }
  return out;
}

/**
 * Standalone React equivalent of DProductTreePreviewModal (Blazor).
 *
 * Architecture: one initial fetch loads the full tree + every product it can resolve to.
 * Every click after that is a pure in-memory tree walk — instant, no network. Unity is
 * told what to render via the product-viewer message contract:
 *   - LoadProduct({ dmodelAssetId, bindings }) when the resolved product changes
 *   - SetSurfaceMaterials({ bindings })        when only material picks change
 */
export function ProductTreePreviewTool(props: ProductTreePreviewToolProps) {
  const [data, setData] = useState<EmbedTreeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [path, setPath] = useState<string[]>([]);
  const [prevFullPath, setPrevFullPath] = useState<string[]>([]);
  const [materialPicks, setMaterialPicks] = useState<Record<string, string>>({});

  // Latest LoadError from Unity, if any.
  const [unityError, setUnityError] = useState<string | null>(null);

  // Ref to the Unity wrapper so we can push messages from effects.
  const viewerRef = useRef<ConfiguratorViewerHandle | null>(null);
  const [viewerReady, setViewerReady] = useState(false);

  // ── Fetch once on mount / when params change ────────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchTree({
      baseUrl: props.baseUrl,
      treeId: props.treeId,
      target: props.target,
      stage: props.stage,
      includeUnpublished: props.includeUnpublished,
      signal: ctrl.signal,
    })
      .then(resp => {
        setData(resp);
        setPath([]);
        setPrevFullPath([]);
        setMaterialPicks({});
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setError(err.message ?? String(err));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [props.baseUrl, props.treeId, props.target, props.stage, props.includeUnpublished]);

  // ── Index tree + product lookup ─────────────────────────────────────
  const idx = useMemo(() => (data ? indexTree(data.nodes) : null), [data]);
  const productById = useMemo(() => {
    const m = new Map<string, EmbedProductDto>();
    for (const p of data?.products ?? []) m.set(p.id, p);
    return m;
  }, [data]);

  // ── Walk locally on every render ────────────────────────────────────
  const walk = useMemo(() => {
    if (!idx || !data) return null;
    let prefs: Map<string, string> | undefined;
    if (prevFullPath.length > 0) {
      const prevWalk = autoResolve(idx, prevFullPath);
      prefs = buildPreferenceMap(prevWalk.decisionsVisited, prevWalk.fullPath, idx);
    }
    return autoResolve(idx, path, prefs);
  }, [idx, data, path, prevFullPath]);

  const resolvedProduct = walk?.resolvedProductId
    ? productById.get(walk.resolvedProductId) ?? null
    : null;

  // ── Seed material picks from product defaults when resolved id changes ──
  useEffect(() => {
    if (!resolvedProduct) return;
    (window as any).DConfigAnalytics?.track?.("product.viewed", { productId: resolvedProduct.id });
    setMaterialPicks(prev => {
      const seed: Record<string, string> = {};
      for (const s of resolvedProduct.surfaces) {
        if (s.defaultMaterialId) seed[s.surfaceKey] = s.defaultMaterialId;
      }
      // Carry user picks where the new product has the same surfaceKey + valid material.
      for (const [k, v] of Object.entries(prev)) {
        const surface = resolvedProduct.surfaces.find(s => s.surfaceKey === k);
        if (surface && surface.materials.some(m => m.id === v)) seed[k] = v;
      }
      return seed;
    });
  }, [resolvedProduct?.id]);

  // ── Push to Unity ───────────────────────────────────────────────────
  // Track what we last sent so the material-only effect doesn't re-fire LoadProduct,
  // and the LoadProduct effect doesn't re-fire when only materials changed.
  const lastSentProductIdRef = useRef<string | null>(null);

  // Whenever the resolved product changes, send LoadProduct with current bindings.
  useEffect(() => {
    if (!viewerReady) return;
    const viewer = viewerRef.current;
    if (!viewer || !resolvedProduct) return;
    if (lastSentProductIdRef.current === resolvedProduct.id) return;

    const bindings = buildBindings(resolvedProduct, materialPicks);
    const payload = JSON.stringify({
      dmodelAssetId: resolvedProduct.modelAssetId,
      bindings,
    });
    setUnityError(null);
    lastSentProductIdRef.current = resolvedProduct.id;
    viewer.sendToUnity("LoadProduct", payload).catch(err =>
      console.warn("[ProductTreePreview] LoadProduct failed:", err)
    );
    // materialPicks intentionally not in deps: a freshly resolved product gets the
    // current picks bundled into LoadProduct. Subsequent material-only changes are
    // handled by the SetSurfaceMaterials effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerReady, resolvedProduct?.id]);

  // When only material picks change for the already-loaded product, send SetSurfaceMaterials.
  useEffect(() => {
    if (!viewerReady) return;
    const viewer = viewerRef.current;
    if (!viewer || !resolvedProduct) return;
    // Skip the initial run for a newly loaded product — LoadProduct already carried
    // the bindings. Only fire when the resolved product matches the last sent.
    if (lastSentProductIdRef.current !== resolvedProduct.id) return;

    const bindings = buildBindings(resolvedProduct, materialPicks);
    const payload = JSON.stringify({ bindings });
    viewer.sendToUnity("SetSurfaceMaterials", payload).catch(err =>
      console.warn("[ProductTreePreview] SetSurfaceMaterials failed:", err)
    );
  }, [viewerReady, materialPicks, resolvedProduct]);

  // ── Click handlers ──────────────────────────────────────────────────
  const handlePickOption = useCallback(
    (decisionIndex: number, optionId: string) => {
      if (!walk) return;
      // Build the new prefix from the *currently visible* walk, not from `path`.
      // Reason: on initial render `path` is empty but the walk has been auto-filled,
      // so picking at row N>0 needs the auto-picked options from rows 0..N-1 as
      // explicit prefix, otherwise AutoResolve sees "[optionId]" as a row-0 pick
      // and the row-N pick gets dropped.
      setPrevFullPath(walk.fullPath);
      const next = walk.fullPath.slice(0, decisionIndex);
      next.push(optionId);
      setPath(next);
    },
    [walk]
  );

  const handlePickMaterial = useCallback((surfaceKey: string, materialId: string) => {
    setMaterialPicks(prev => ({ ...prev, [surfaceKey]: materialId }));
  }, []);

  const handleUnityEvent = useCallback((eventType: string, data: string) => {
    if (eventType === "ProductLoaded") {
      // Unity reports back the surface keys it actually exposes — could reconcile
      // here if our DTO drifts from the model's real surfaces. For now just log.
      console.info("[ProductTreePreview] Unity ProductLoaded:", data);
      setUnityError(null);
      return;
    }
    if (eventType === "LoadError") {
      try {
        const parsed = JSON.parse(data) as { error?: string };
        setUnityError(parsed.error ?? data);
      } catch {
        setUnityError(data);
      }
    }
  }, []);

  if (loading && !data) {
    return <div className="dptp-state dptp-state--loading">Loading tree…</div>;
  }
  if (error) {
    return <div className="dptp-state dptp-state--error">Error: {error}</div>;
  }
  if (!data || !walk) {
    return <div className="dptp-state dptp-state--empty">No data.</div>;
  }

  const treeName = data.treeName || "(unnamed tree)";

  return (
    <div className="dptp">
      <div className="dptp__row">
        {/* Viewer column (left, ~7/12) */}
        <div className="dptp__col-viewer">
          <div className="dptp__viewer-card">
            {/*
              Render priority:
                1. Unity viewer — when the tree was loaded with target/stage AND the
                   resolved product has a 3D model to render.
                2. Product image  — the resolved product's own main image.
                3. Tree image     — fallback set on the tree itself.
                4. 📦 placeholder — nothing else to show.
            */}
            {data.viewer && resolvedProduct?.modelAssetId ? (
              <ConfiguratorViewer
                ref={viewerRef}
                viewer={data.viewer}
                baseUrl={props.baseUrl}
                apiBaseUrl={props.apiBaseUrl}
                onReady={() => setViewerReady(true)}
                onUnityEvent={handleUnityEvent}
              />
            ) : resolvedProduct?.imageUrl ? (
              <div className="dptp__viewer-image">
                <img src={resolvedProduct.imageUrl} alt={resolvedProduct.name} />
              </div>
            ) : data.treeImageUrl ? (
              <div className="dptp__viewer-image">
                <img src={data.treeImageUrl} alt={data.treeName} />
              </div>
            ) : resolvedProduct ? (
              <div className="dptp__viewer-image">
                <div className="dptp__viewer-icon">📦</div>
              </div>
            ) : (
              <div className="dptp__viewer-empty">
                <div className="dptp__viewer-icon">📦</div>
                <p>No product resolved yet.</p>
                {walk.error && <p className="dptp__viewer-warning">{walk.error}</p>}
              </div>
            )}
            {unityError && (
              <div className="dptp__viewer-error-banner">{unityError}</div>
            )}
          </div>
        </div>

        {/* Choices column (right, ~5/12) */}
        <div className="dptp__col-choices">
          <header className="dptp__header">
            <h2 className="dptp__title">{treeName}</h2>
            {resolvedProduct && <p className="dptp__subtitle">{resolvedProduct.name}</p>}
          </header>

          {walk.decisionsVisited.length === 0 || !idx ? (
            <p className="dptp__muted">This tree has no decisions yet.</p>
          ) : (
            walk.decisionsVisited.map((decision, decisionIndex) => {
              const pickedId =
                decisionIndex < walk.fullPath.length ? walk.fullPath[decisionIndex] : null;
              const options = idx.childrenOf.get(decision.id) ?? [];
              return (
                <section className="dptp__decision" key={decision.id}>
                  <h6 className="dptp__decision-heading">{decision.label}</h6>
                  <div className="dptp__options">
                    {options.map(option => {
                      const isSelected = option.id === pickedId;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          className={
                            "dptp__option" + (isSelected ? " dptp__option--selected" : "")
                          }
                          data-track-key="options.variant"
                          onClick={() => {
                            (window as any).DConfigAnalytics?.track?.("variant.changed", { decisionId: decision.id, optionId: option.id });
                            handlePickOption(decisionIndex, option.id);
                          }}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })
          )}

          {/* Material picks rendered as additional decisions, matching the Blazor modal.
              Locked surfaces are applied at render time but excluded from the picker UI. */}
          {resolvedProduct && resolvedProduct.surfaces.some(s => !s.locked) && (
            <>
              <hr className="dptp__divider" />
              {resolvedProduct.surfaces.filter(s => !s.locked).map(surface => {
                const current = materialPicks[surface.surfaceKey] ?? surface.defaultMaterialId;
                return (
                  <section className="dptp__decision" key={surface.surfaceKey}>
                    <h6 className="dptp__decision-heading">{surface.surfaceLabel}</h6>
                    <div className="dptp__options">
                      {surface.materials.length === 0 && (
                        <span className="dptp__muted dptp__muted--small">
                          No materials in this collection.
                        </span>
                      )}
                      {surface.materials.map(m => {
                        const isSelected = current === m.id;
                        return (
                          <button
                            key={m.id}
                            type="button"
                            className={
                              "dptp__option" + (isSelected ? " dptp__option--selected" : "")
                            }
                            data-track-key="options.material-swatch"
                            onClick={() => {
                              (window as any).DConfigAnalytics?.track?.("material.changed", { surfaceKey: surface.surfaceKey, materialId: m.id, previousMaterialId: current ?? null });
                              handlePickMaterial(surface.surfaceKey, m.id);
                            }}
                          >
                            {m.name}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductTreePreviewTool;
